import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { HandsetDialogService } from './shared/handset-dialog.service';
import { LoadingComponent } from './components/shared/ui/loading/loading.component';
import { ShareDialogComponent } from './components/project/pages/preview/share-dialog/share-dialog.component';
import { ProjectSelectionDialogComponent } from './components/shared/dialogs/project-selection-dialog/project-selection-dialog.component';
import {
  NewProjectDialogComponent,
  NewProjectData,
} from './components/project/settings/new-project-dialog/new-project-dialog.component';
import {
  ProjectCopyDialogComponent,
  ProjectCopyResult,
  ProjectCopyDialogData,
} from './components/shared/dialogs/project-copy-dialog/project-copy-dialog.component';
import {
  ProjectDeleteDialogComponent,
  ProjectDeleteResult,
  ProjectDeleteDialogData,
} from './components/shared/dialogs/project-delete-dialog/project-delete-dialog.component';
import { GlobalProgressComponent } from './components/shared/ui/global-progress/global-progress.component';
import { AdminTopAppBarComponent } from './components/shared/ui/admin-top-app-bar/admin-top-app-bar.component';
import { ProjectTopAppBarComponent } from './components/shared/ui/project-top-app-bar/project-top-app-bar.component';
import { LandingTopAppBarComponent } from './components/shared/ui/landing-top-app-bar/landing-top-app-bar.component';
import { BillingTopAppBarComponent } from './components/billing/billing-top-app-bar/billing-top-app-bar.component';
// Chat components (Issue #263)
import { ChatComponent } from './components/chat/chat.component';
import { ChatStateService } from './services/chat-state.service';
import { UiService } from './shared/ui.service';
import { FirebaseAuthService } from './shared/firebase-auth.service';
import { HandoffFailure } from './shared/auth-handoff';
import { GlobalTaskNotificationService } from './shared/global-task-notification.service';
import { PlanReviewerService } from './shared/plan-reviewer.service';
import { ProjectCreationService } from './shared/project-creation.service';
import { ProjectCopyService } from './shared/project-copy.service';
import { ProjectDeleteService } from './shared/project-delete.service';
import { ClientLoggingService } from './shared/client-logging.service';
import { BillingRefreshService } from './shared/billing-refresh.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, merge, of, Subscription, from, combineLatest } from 'rxjs';
import { User } from 'firebase/auth';
import { filter, map, distinctUntilChanged, switchMap, catchError, startWith, shareReplay } from 'rxjs/operators';

/** Root application shell that hosts the top app bar, navigation drawer, route outlet, and the AI chat panel. */
@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    LoadingComponent,
    GlobalProgressComponent,
    AdminTopAppBarComponent,
    ProjectTopAppBarComponent,
    LandingTopAppBarComponent,
    BillingTopAppBarComponent,
    // Chat components (Issue #263)
    ChatComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  private uiService = inject(UiService);
  auth = inject(FirebaseAuthService);
  private dialog = inject(MatDialog);
  private handsetDialog = inject(HandsetDialogService);
  private router = inject(Router);
  private globalTaskNotificationService = inject(GlobalTaskNotificationService);
  private planReviewerService = inject(PlanReviewerService);
  private projectCreationService = inject(ProjectCreationService);
  private projectCopyService = inject(ProjectCopyService);
  private projectDeleteService = inject(ProjectDeleteService);
  private snackBar = inject(MatSnackBar);
  private chatState = inject(ChatStateService);
  private billingRefreshService = inject(BillingRefreshService);
  loading$: Observable<boolean>;
  unauthorized$: Observable<boolean>;
  needsSignIn$: Observable<boolean>;
  /** True when sign-in is waiting for a click because the handoff needs a popup (issue #1958). */
  signInRequiresGesture$: Observable<boolean>;
  /** Recoverable sign-in message, shown beside the sign-in control. */
  signInNotice$: Observable<string | null>;
  /**
   * Set when sign-in has stopped and owes the user an explanation rather than
   * another attempt (issue #1958): a credential handoff that kept coming back
   * empty, or a sign-in helper this origin cannot reach.
   */
  signInBlocked$: Observable<HandoffFailure | null>;
  user$: Observable<User | null>;
  authorizationError$: Observable<string | null>;
  showProjectUI$: Observable<boolean>;
  isAdminRoute$: Observable<boolean>;
  isProjectRoute$: Observable<boolean>;
  isLandingRoute$: Observable<boolean>;
  isBillingRoute$: Observable<boolean>;
  isUnauthorizedRoute$: Observable<boolean>;
  currentProjectId$: Observable<string> = of('');
  currentProjectTitle$: Observable<string> = of('');

  private projectIdSubscription?: Subscription;

  private clientLoggingService = inject(ClientLoggingService);

  constructor() {
    this.loading$ = this.auth.loading$;
    this.unauthorized$ = this.auth.unauthorized$;
    this.needsSignIn$ = this.auth.needsSignIn$;
    this.signInRequiresGesture$ = this.auth.signInRequiresGesture$;
    this.signInNotice$ = this.auth.signInNotice$;
    this.signInBlocked$ = this.auth.signInBlocked$;
    this.user$ = this.auth.user$;
    this.authorizationError$ = this.auth.authorizationError$;

    // Temporary: Expose service for manual verification during Issue #724 fix
    (window as any).logger = this.clientLoggingService;
    ClientLoggingService.log('INFO', '[AppComponent] ClientLoggingService exposed as window.logger');

    // Detect different route types
    this.isAdminRoute$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url.startsWith('/admin')),
      startWith(this.router.url.startsWith('/admin')), // Emit initial value immediately
      distinctUntilChanged(),
      map(isAdmin => {
        ClientLoggingService.log('INFO', `[AppComponent] Admin route status: ${isAdmin} (URL: ${this.router.url})`);
        return isAdmin;
      })
    );

    // Wait for router to be ready before checking routes
    const routerReady$ = new Observable<boolean>(subscriber => {
      // Use setTimeout to ensure router has completed initial navigation
      setTimeout(() => {
        const isProject = this.router.url.startsWith('/projects/');
        ClientLoggingService.log(
          'INFO',
          `[AppComponent] Project route INITIAL check (after router ready): ${isProject} (URL: "${this.router.url}")`
        );
        subscriber.next(isProject);
        subscriber.complete();
      }, 0);
    });

    this.isProjectRoute$ = merge(
      routerReady$,
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          const isProject = this.router.url.startsWith('/projects/');
          ClientLoggingService.log(
            'INFO',
            `[AppComponent] Project route check: ${isProject} (URL: "${this.router.url}")`
          );
          return isProject;
        })
      )
    ).pipe(
      distinctUntilChanged(),
      shareReplay(1) // Cache the latest value
    );

    // Billing route detection - use same pattern as isProjectRoute$ for consistency
    const billingRouterReady$ = new Observable<boolean>(subscriber => {
      setTimeout(() => {
        const isBilling = this.router.url.startsWith('/billing');
        ClientLoggingService.log(
          'INFO',
          `[AppComponent] Billing route INITIAL check (after router ready): ${isBilling} (URL: "${this.router.url}")`
        );
        subscriber.next(isBilling);
        subscriber.complete();
      }, 0);
    });

    this.isBillingRoute$ = merge(
      billingRouterReady$,
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          const isBilling = this.router.url.startsWith('/billing');
          ClientLoggingService.log(
            'INFO',
            `[AppComponent] Billing route check: ${isBilling} (URL: "${this.router.url}")`
          );
          return isBilling;
        })
      )
    ).pipe(
      distinctUntilChanged(),
      shareReplay(1) // Cache the latest value
    );

    // Detect unauthorized/error routes (Issue #304)
    const unauthorizedRouterReady$ = new Observable<boolean>(subscriber => {
      setTimeout(() => {
        // Check both router URL and window location to be robust during initial load
        const path = this.router.url;
        const locationPath = window.location.pathname;

        const isUnauth =
          path.startsWith('/unauthorized') ||
          path.startsWith('/error') ||
          path.startsWith('/no-access') ||
          path.startsWith('/signed-out') ||
          locationPath.startsWith('/signed-out'); // Fallback for initial load

        ClientLoggingService.log(
          'INFO',
          `[AppComponent] Unauthorized route INITIAL check: ${isUnauth} (Router: "${path}", Window: "${locationPath}")`
        );
        subscriber.next(isUnauth);
        subscriber.complete();
      }, 0);
    });

    this.isUnauthorizedRoute$ = merge(
      unauthorizedRouterReady$,
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map((event: any) => {
          const url = event.urlAfterRedirects || event.url || this.router.url;
          const isUnauth =
            url.startsWith('/unauthorized') ||
            url.startsWith('/error') ||
            url.startsWith('/no-access') ||
            url.startsWith('/signed-out');
          ClientLoggingService.log('INFO', `[AppComponent] Unauthorized route check: ${isUnauth} (URL: "${url}")`);
          return isUnauth;
        })
      )
    ).pipe(
      distinctUntilChanged(),
      shareReplay(1) // Cache the latest value
    );

    this.isLandingRoute$ = combineLatest([
      this.isAdminRoute$,
      this.isProjectRoute$,
      this.isBillingRoute$,
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.router.url.startsWith('/print-preview')),
        startWith(window.location.pathname.startsWith('/print-preview'))
      ),
    ]).pipe(
      map(([isAdmin, isProject, isBilling, isPrint]) => {
        const isLanding = !isAdmin && !isProject && !isBilling && !isPrint;
        ClientLoggingService.log('INFO', `[AppComponent] Landing route status: ${isLanding} (URL: ${this.router.url})`);
        return isLanding;
      })
    );

    this.showProjectUI$ = combineLatest([this.loading$, this.authorizationError$, this.user$, this.isAdminRoute$]).pipe(
      map(([loading, authError, user, isAdmin]) => {
        const showUI = !loading && !authError && !!user && !isAdmin;
        ClientLoggingService.log('INFO', `[AppComponent] ShowProjectUI check:`, {
          loading,
          authError: !!authError,
          user: !!user,
          isAdmin,
          showUI,
        });
        return showUI;
      })
    );
  }

  ngOnInit() {
    // Diagnostic log for Issue #724
    this.clientLoggingService.log('INFO', 'AppComponent INITIALIZED: Logging bridge active', 'Diagnostic');

    // Track project navigation for both share button and task notifications
    const navigationEvents$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.extractProjectIdFromUrl())
    );

    // Check initial URL after router is initialized
    const initialCheck$ = new Observable<string>(subscriber => {
      setTimeout(() => {
        const projectId = this.extractProjectIdFromUrl();
        subscriber.next(projectId);
        subscriber.complete();
      }, 0);
    });

    // Create project ID observable
    const projectId$ = merge(initialCheck$, navigationEvents$).pipe(
      distinctUntilChanged() // Only emit when project ID actually changes
    );

    // Set up current project ID for template
    this.currentProjectId$ = projectId$.pipe(
      map(projectId => {
        ClientLoggingService.log('INFO', `[AppComponent] Current project ID for template: ${projectId}`);
        return projectId || '';
      }),
      shareReplay(1) // Cache the latest value for new subscribers
    );

    // Set up project title observable
    this.currentProjectTitle$ = projectId$.pipe(
      switchMap(projectId => {
        if (projectId) {
          ClientLoggingService.log('INFO', `[AppComponent] Loading project title for: ${projectId}`);
          // Try to get project name from metadata first
          return from(this.projectCreationService.getProjectMetadata(projectId)).pipe(
            map((result: any) => {
              if (result.success && result.metadata?.project_name) {
                ClientLoggingService.log('INFO', `[AppComponent] Using project name: ${result.metadata.project_name}`);
                return result.metadata.project_name;
              } else {
                ClientLoggingService.log('INFO', `[AppComponent] No project name found, using projectId`);
                return projectId;
              }
            }),
            catchError(error => {
              ClientLoggingService.log(
                'WARN',
                `[AppComponent] Failed to load project metadata for: ${projectId}, using projectId as fallback`,
                error
              );
              return of(projectId);
            })
          );
        } else {
          ClientLoggingService.log('INFO', `[AppComponent] No project ID found, clearing title`);
          return of('');
        }
      }),
      shareReplay(1)
    );

    // Subscribe to project changes for task notifications
    this.projectIdSubscription = projectId$.subscribe(projectId => {
      if (projectId) {
        ClientLoggingService.log(
          'INFO',
          `[AppComponent] Navigated to project: ${projectId} - starting task notifications`
        );
        this.globalTaskNotificationService.startListening(projectId);
      } else {
        ClientLoggingService.log('INFO', `[AppComponent] Left project context - stopping task notifications`);
        this.globalTaskNotificationService.stopListening();
      }
    });
  }

  private extractProjectIdFromUrl(): string {
    const urlParts = this.router.url.split('/');
    const projectIndex = urlParts.indexOf('projects');
    const rawProjectId = projectIndex !== -1 && urlParts[projectIndex + 1] ? urlParts[projectIndex + 1] : '';
    // Decode URL-encoded characters (e.g., %20 becomes space)
    return rawProjectId ? decodeURIComponent(rawProjectId) : '';
  }

  toggleDrawer() {
    this.uiService.toggleDrawer();
  }

  toggleChat() {
    this.chatState.toggleChat();
  }

  signIn() {
    this.auth.signIn();
  }

  /**
   * Tries sign-in again after the user has read why it stopped (issue #1958).
   * Distinct from `signIn` because it first clears the explanation, so a second
   * failure re-renders the panel rather than leaving a stale one on screen.
   */
  retrySignIn() {
    this.auth.retrySignIn();
  }

  signOut() {
    this.auth.signOut();
  }

  ngOnDestroy() {
    // Clean up subscriptions
    if (this.projectIdSubscription) {
      this.projectIdSubscription.unsubscribe();
    }
    this.globalTaskNotificationService.stopListening();
  }

  switchAccount() {
    // Sign out and then trigger sign in with account selection
    this.auth.signOut().then(() => {
      this.auth.signIn();
    });
  }

  openShareDialog() {
    // Get current project ID from URL
    const projectId = this.extractProjectIdFromUrl();

    this.dialog.open(ShareDialogComponent, {
      width: '600px',
      data: {
        projectId: projectId,
      },
    });
  }

  openProjectCopyDialog() {
    // Get current project ID from URL
    const projectId = this.extractProjectIdFromUrl();

    if (!projectId) {
      this.snackBar.open('No project selected', 'Close', { duration: 3000 });
      return;
    }

    ClientLoggingService.log('INFO', '[AppComponent] Opening project copy dialog for project:', projectId);

    const dialogRef = this.dialog.open(ProjectCopyDialogComponent, {
      width: '600px',
      data: {
        projectId: projectId,
        projectName: `Project ${projectId}`, // TODO: Get actual project name from metadata
      } as ProjectCopyDialogData,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe(async (result: ProjectCopyResult | undefined) => {
      if (result && result.success) {
        await this.handleProjectCopy(result);
      }
    });
  }

  createNewProject() {
    ClientLoggingService.log('INFO', '[AppComponent] Opening new project dialog...');

    const dialogRef = this.handsetDialog.open(NewProjectDialogComponent, {
      width: '600px',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe(async (result: NewProjectData | undefined) => {
      if (result) {
        await this.handleCreateProject(result);
      }
    });
  }

  async openProjectSelector() {
    try {
      ClientLoggingService.log('INFO', '[AppComponent] Opening project selector...');

      const dialogRef = ProjectSelectionDialogComponent.open(this.dialog, { showCancel: true });

      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          if (result.action === 'new-project') {
            await this.createNewProject();
          } else if (result.action === 'no-projects') {
            await this.createNewProject();
          } else if (result.architecturalProjectId) {
            ClientLoggingService.log('INFO', `[AppComponent] Project selected: ${result.architecturalProjectId}`);
            this.router.navigate(['/projects', result.architecturalProjectId, 'pages', '1', 'overview']);
          }
        }
      });
    } catch (error) {
      ClientLoggingService.log('ERROR', '[AppComponent] Error opening project selector:', error);
    }
  }

  openProjectSettings() {
    const projectId = this.extractProjectIdFromUrl();
    if (projectId) {
      this.router.navigate(['/projects', projectId, 'settings']);
    }
  }

  navigateToTasks() {
    const projectId = this.extractProjectIdFromUrl();
    if (projectId) {
      this.router.navigate(['/projects', projectId, 'tasks']);
    }
  }

  private async handleCreateProject(projectData: NewProjectData) {
    // The new project dialog now handles the entire workflow internally
    // including async task progress tracking. If we get here, it means
    // the user clicked "Go to Project" and we should navigate.

    if (projectData.projectId) {
      ClientLoggingService.log('INFO', `[AppComponent] Navigating to project: ${projectData.projectId}`);
      this.router.navigate(['/projects', projectData.projectId, 'pages', '1', 'overview']);
    }
  }

  private async handleProjectCopy(result: ProjectCopyResult) {
    try {
      ClientLoggingService.log('INFO', `[AppComponent] Starting project copy from dialog result:`, result);

      if (!result.targetProjectId) {
        this.snackBar.open('Invalid project copy result', 'Close', { duration: 3000 });
        return;
      }

      // Show loading message
      this.snackBar.open('Copying project...', 'Close', { duration: 2000 });

      // Call the project copy service
      const copyOptions = {
        sourceProjectId: this.extractProjectIdFromUrl(),
        targetProjectId: result.targetProjectId,
        copySharingSettings: result.copySharingSettings || false,
      };

      this.projectCopyService.copyProject(copyOptions).subscribe({
        next: copyResult => {
          ClientLoggingService.log('INFO', '[AppComponent] Project copy completed:', copyResult);

          if (copyResult.success) {
            this.snackBar
              .open(`Project copied successfully! ${copyResult.filesCopied} files copied.`, 'Go to Project', {
                duration: 8000,
              })
              .onAction()
              .subscribe(() => {
                // Navigate to the new project
                this.router.navigate(['/projects', copyResult.targetProjectId, 'pages', '1', 'overview']);
              });
          } else {
            this.snackBar.open(copyResult.error || 'Failed to copy project', 'Close', { duration: 5000 });
          }
        },
        error: error => {
          ClientLoggingService.log('ERROR', '[AppComponent] Error copying project:', error);
          this.snackBar.open(error.error || 'Failed to copy project', 'Close', { duration: 5000 });
        },
      });
    } catch (error) {
      ClientLoggingService.log('ERROR', '[AppComponent] Error handling project copy:', error);
      this.snackBar.open('Error copying project', 'Close', { duration: 3000 });
    }
  }

  openProjectDeleteDialog() {
    // Get current project ID from URL
    const projectId = this.extractProjectIdFromUrl();

    if (!projectId) {
      this.snackBar.open('No project selected', 'Close', { duration: 3000 });
      return;
    }

    ClientLoggingService.log('INFO', '[AppComponent] Opening project delete dialog for project:', projectId);

    const dialogRef = this.dialog.open(ProjectDeleteDialogComponent, {
      width: '600px',
      data: {
        projectId: projectId,
        projectName: `Project ${projectId}`, // TODO: Get actual project name from metadata
        fileCount: undefined, // TODO: Get from backend
        totalSizeBytes: undefined, // TODO: Get from backend
        estimatedCost: undefined, // Future enhancement
        showHardDeleteOption: this.isTestOrDevEnvironment(),
      } as ProjectDeleteDialogData,
      disableClose: false, // Allow click-outside and escape key to close
      hasBackdrop: true, // Show backdrop for click-outside dismissal
      closeOnNavigation: true, // Allow closing on navigation
    });

    dialogRef.afterClosed().subscribe(async (result: ProjectDeleteResult | undefined) => {
      if (result && result.success) {
        await this.handleProjectDelete(result);
      }
    });
  }

  private async handleProjectDelete(result: ProjectDeleteResult) {
    try {
      ClientLoggingService.log('INFO', `[AppComponent] Project deletion completed from dialog:`, result);

      if (!result.projectId) {
        this.snackBar.open('Invalid project deletion result', 'Close', { duration: 3000 });
        return;
      }

      if (result.success) {
        // Show success message
        this.snackBar.open(
          `Project "${result.projectId}" deleted successfully! ${result.filesDeleted} files removed.`,
          'Close',
          { duration: 8000 }
        );

        // Navigate to project list since current project no longer exists
        this.router.navigate(['/projects']);
      } else {
        // Error handling is now done in the dialog itself
        ClientLoggingService.log('INFO', '[AppComponent] Project deletion was not successful');
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[AppComponent] Error handling project deletion:', error);
      this.snackBar.open('Error deleting project', 'Close', { duration: 3000 });
    }
  }

  /**
   * Check if current environment is test or development
   */
  private isTestOrDevEnvironment(): boolean {
    const hostname = window.location.hostname;
    const port = window.location.port;

    return hostname.includes('test') || hostname.includes('dev') || hostname.includes('localhost') || port === '4200';
  }

  onRefreshBilling(): void {
    ClientLoggingService.log('INFO', '[AppComponent] Refreshing billing data');
    this.billingRefreshService.requestRefresh();
  }
}
